import React, { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@/components/modal/Modal';
import { UnsavedChangesModal } from '@/components/unsavedChanges/UnsavedChangesModal';
import { ModalProps } from '@/components/modal/useModal';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Form } from '@/components/form/Form';
import { TextField } from '@/components/form/fields/text/TextInput';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { EmailField } from '@/components/form/fields/text/EmailInput';
import { DateField } from '@/components/form/fields/date/DateInput';
import { CheckboxField } from '@/components/form/fields/checkbox/Checkbox';
import { AddressBookSelector } from '@/components/form/fields/address/AddressBookSelector';
import { AddressRequest } from '@/services/api/contracts/address';
import { Card } from '@/components/sections/Card';
import { useCreateServiceRequest } from '@/services/api/hooks/serviceRequests';
import { useUser } from '@/services/authentication/hooks/user';
import { useCompany, useMspDetailsForCustomer } from '@/services/api/hooks/companies';
import { toast } from 'sonner';

const BaseDeliveryFormSchema = z.object({
  userName: z.string().min(1, 'Required'),
  corporateEmail: z.string().email('Invalid email').or(z.literal('')).or(z.literal('na')),
  personalEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  noCorporateEmail: z.boolean().default(false),
  requiredByDate: z.date(),
  address: AddressRequest.omit({ name: true }),
  deliveryContactName: z.string().min(1, 'Required'),
  deliveryContactEmail: z.string().email('Invalid email'),
  deliveryContactMobile: z.string().min(1, 'Required'),
  customerReference: z.string().optional(),
  notes: z.string().optional()
});

const DeliveryFormSchema = BaseDeliveryFormSchema.superRefine((data, ctx) => {
  if (!data.noCorporateEmail && (!data.corporateEmail || data.corporateEmail.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required unless checked",
      path: ["corporateEmail"],
    });
  }
});

type DeliveryFormFields = z.infer<typeof DeliveryFormSchema>;

interface DeployInventoryDeliveryModalProps extends ModalProps {
  mspId: string;
  customerId: string;
  cart: Record<string, number>;
  selectedBundleId: string | null;
  invoiceItems: Array<{ name: string; quantity: number; price: number; total: number }>;
  deployedHardware: Array<{ name: string; quantity: number; sku: string }>;
  totalCost: number;
  backorderItems: Array<{ name: string; quantity: number; sku?: string }>;
  onSuccessCallback: () => void;
}

export const DeployInventoryDeliveryModal: React.FC<DeployInventoryDeliveryModalProps> = ({ mspId, customerId, cart, selectedBundleId, invoiceItems, deployedHardware, totalCost, backorderItems, onSuccessCallback, ...props }) => {
  const createServiceRequest = useCreateServiceRequest();
  const user = useUser();
  const [partner] = useMspDetailsForCustomer(mspId, customerId);
  const [company] = useCompany(mspId, customerId);
  const methods = useForm<DeliveryFormFields>({
    resolver: zodResolver(DeliveryFormSchema),
    defaultValues: {
      userName: '',
      corporateEmail: '',
      personalEmail: '',
      noCorporateEmail: false,
      deliveryContactName: '',
      deliveryContactEmail: '',
      deliveryContactMobile: '',
      customerReference: '',
      notes: ''
    }
  });

  useEffect(() => {
    if (props.isOpen) {
      methods.reset({
        userName: '',
        corporateEmail: '',
        personalEmail: '',
        noCorporateEmail: false,
        deliveryContactName: '',
        deliveryContactEmail: '',
        deliveryContactMobile: '',
        customerReference: '',
        notes: ''
      });
    }
  }, [props.isOpen, methods]);

  const noCorporateEmail = methods.watch('noCorporateEmail');

  useEffect(() => {
    if (noCorporateEmail) {
      methods.setValue('corporateEmail', 'na', { shouldValidate: true });
    } else {
      // Only clear it if it was explicitly 'na'
      if (methods.getValues('corporateEmail') === 'na') {
        methods.setValue('corporateEmail', '', { shouldValidate: true });
      }
    }
  }, [noCorporateEmail, methods]);

  const handleAddressChange = useCallback((addressPayload: any) => {
    methods.setValue('address', addressPayload, { shouldValidate: true, shouldDirty: true });
  }, [methods]);

  const onSubmit = async (data: DeliveryFormFields) => {
    const finalCustomerReference = data.customerReference?.trim() || data.userName || 'No Reference Provided';

    const logisticsDetails = {
      ...data,
      customerReference: finalCustomerReference,
      requiredByDate: data.requiredByDate ? new Date(data.requiredByDate).toISOString() : undefined,
      address: {
        ...data.address,
        name: 'Delivery Address'
      }
    };

    const details = {
      requestType: 'DeployInventory',
      requestedBy: user?.email || 'Unknown',
      mspId,
      mspName: partner?.name || '',
      customerId,
      customerName: company?.name || '',
      raisedDateTime: new Date().toISOString(),
      customerReference: finalCustomerReference,
      submissionSource: 'DaaS Portal UI',
      totalEstimatedCost: totalCost,
      haloClientId: company?.haloClientId || null,
      haloGeneralUserId: company?.haloGeneralUserId || null
    };

    const finalPayload = {
      details,
      logisticsDetails,
      deployedItems: {
        cart,
        selectedBundleId,
        backorderedItems: backorderItems
      },
      inferredServices: invoiceItems.map(item => ({
        name: item.name,
        quantity: item.quantity,
        price: Math.round(item.price * 100)
      })),
      totalCost
    };
    
    try {
      await createServiceRequest.mutateAsync({
        params: { mspId, customerId },
        body: {
          requestedBy: user?.email || 'Unknown',
          requestType: 'DeployInventory',
          customerReference: finalCustomerReference,
          payload: finalPayload
        }
      });
      
      toast.success('Service Request Logged Successfully');
      onSuccessCallback();
      props.close();
    } catch (e) {
      toast.error('Failed to log request. Please try again.');
    }
  };

  const { isDirty } = methods.formState;
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else {
      props.close();
    }
  };

  const guardedProps = { ...props, close: handleClose };

  return (
    <>
      <Modal className="md:max-w-5xl lg:max-w-6xl flex flex-col h-[90vh]" {...guardedProps}>
      <Card className="w-full h-full flex flex-col overflow-hidden bg-white">
        <div className="p-8 pb-4 shrink-0">
          <Modal.Controls {...guardedProps}>
            <h1 className="text-2xl font-semibold">Deploy Inventory</h1>
          </Modal.Controls>
          <p className="text-gray-600">Please provide the final shipping and contact details for this deployment.</p>
        </div>
        
        <Form methods={methods} onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Left Column: The Receipt */}
              <div className="space-y-6">
                <div className="flex flex-col border border-gray-200 rounded bg-gray-50 p-6 h-fit">
                  <h3 className="text-lg font-bold border-b pb-3 mb-4">Deployment Summary</h3>
                  
                  {((deployedHardware && deployedHardware.length > 0) || (backorderItems && backorderItems.length > 0)) && (
                    <div className="mb-4 bg-white p-3 rounded border border-gray-200 text-sm">
                      <p className="font-semibold text-gray-800 mb-2 border-b pb-1">Hardware to Deploy:</p>
                      
                      {deployedHardware && deployedHardware.length > 0 && (
                        <ul className={`space-y-1 text-gray-600 list-disc list-inside ${backorderItems && backorderItems.length > 0 ? 'mb-3' : ''}`}>
                          {deployedHardware.map((item, index) => (
                            <li key={`hw-${index}`}>
                              <span className="font-medium">{item.quantity}x</span> {item.name} <span className="opacity-75 text-xs ml-1">({item.sku})</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {backorderItems && backorderItems.length > 0 && (
                        <div className={`space-y-2 ${deployedHardware && deployedHardware.length > 0 ? 'pt-2 border-t border-gray-100' : ''}`}>
                          <p className="text-xs font-semibold text-amber-600 uppercase">
                            On Backorder
                            <span className="text-xs italic font-normal normal-case text-gray-500 ml-2">additional fees may apply</span>
                          </p>
                          {backorderItems.map((item, index) => (
                            <div key={`backorder-${index}`} className="flex justify-between items-start text-xs text-amber-800 bg-amber-50 p-2 rounded">
                              <div className="pr-2 flex flex-col">
                                <span><span className="font-medium">{item.quantity}x</span> {item.name}</span>
                                {item.sku && <span className="opacity-75 mt-0.5 ml-4">SKU: {item.sku}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-4 flex-1">
                    {invoiceItems.map((item, index) => (
                      <div key={index} className="flex justify-between items-start text-sm">
                        <div className="pr-4">
                          <span className="font-medium">{item.quantity}x</span> {item.name}
                        </div>
                        <div className="font-medium whitespace-nowrap">
                          {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(item.total)}
                        </div>
                      </div>
                    ))}


                  </div>
                  
                  <div className="flex justify-between items-center pt-4 mt-6 border-t font-bold text-lg">
                    <span>Estimated Total</span>
                    <span>{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(totalCost)}</span>
                  </div>
                </div>

                <div className="border p-4 rounded space-y-4">
                  <div>
                    <TextField id="customerReference" label="Customer Reference" placeholder="This will appear on your invoice..." optional={true} />
                    <p className="text-xs text-gray-500 italic mt-1">If no reference is given, the user's name will be used</p>
                  </div>
                </div>
                
                <div className="border p-4 rounded space-y-4">
                  <TextAreaField id="notes" label="Notes / Special Requirements" optional={true} />
                </div>
              </div>

              {/* Right Column: Logistics & Data Entry */}
              <div className="space-y-6">
                <div className="border p-4 rounded space-y-4">
                  <h4 className="text-lg font-bold">User Details</h4>
                  <TextField id="userName" label="User Name" />
                  <div className="space-y-4">
                    <div className={methods.watch('noCorporateEmail') ? 'opacity-50 pointer-events-none' : ''}>
                      <EmailField 
                        id="corporateEmail" 
                        label={`Corporate Email${methods.watch('noCorporateEmail') ? '' : ' *'}`} 
                        disabled={methods.watch('noCorporateEmail')}
                      />
                    </div>
                    <CheckboxField id="noCorporateEmail" label="User does not have corporate email" />
                    <EmailField id="personalEmail" label="Personal Email (For New Hires)" optional={true} />
                  </div>
                </div>
                
                <div className="border p-4 rounded space-y-4">
                  <h4 className="text-lg font-bold">Delivery Address</h4>
                  <AddressBookSelector 
                    mspId={mspId}
                    customerId={customerId}
                    onChange={handleAddressChange}
                  />
                  {methods.formState.errors.address && (
                    <p className="text-red-500 text-sm mt-1">Please select or provide a valid address.</p>
                  )}
                  <DateField id="requiredByDate" label="Required By Date" />
                </div>

                <div className="border p-4 rounded space-y-4">
                  <h4 className="text-lg font-bold">Delivery Contact</h4>
                  <TextField id="deliveryContactName" label="Delivery Contact Name" />
                  <EmailField id="deliveryContactEmail" label="Delivery Contact Email" />
                  <TextField id="deliveryContactMobile" label="Delivery Contact Mobile" />
                </div>
              </div>

            </div>
          </div>

          <div className="flex justify-end p-6 border-t bg-gray-50 shrink-0">
            <Button variant={ButtonVariant.Ghost} onClick={guardedProps.close} className="mr-4" disabled={createServiceRequest.isLoading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant={ButtonVariant.Primary}
              disabled={createServiceRequest.isLoading}
            >
              {createServiceRequest.isLoading ? 'Submitting...' : 'Confirm Delivery & Submit'}
            </Button>
          </div>
        </Form>
      </Card>
    </Modal>
    <UnsavedChangesModal
      isOpen={showUnsavedWarning}
      open={() => setShowUnsavedWarning(true)}
      close={() => setShowUnsavedWarning(false)}
      onCancelCallback={() => setShowUnsavedWarning(false)}
      onConfirmCallback={() => {
        setShowUnsavedWarning(false);
        props.close();
      }}
      title="Abandon Request"
      message="Your request is partially complete. Are you sure you want to leave? Your progress will be lost."
      confirmText="Yes, Abandon"
      confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
      cancelText="No, Continue"
      cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
    />
    </>
  );
};
